import { tokenService } from '@features/authentication/services/tokenService';
import { refreshAccessToken } from './apiClient';

/**
 * E7 item 4 — refresh the access token, if needed, BEFORE an SSE stream reconnects.
 *
 * ## The bug this closes
 *
 * `EventSource` cannot send headers, so every stream carries its JWT as a `?access_token=` query
 * parameter — captured when the URL is built. A reconnect rebuilds that URL from whatever is in storage,
 * and nothing refreshed it. Access tokens live 15 minutes, so a tab left idle past expiry reconnected
 * with a dead token, was rejected, backed off, and tried the same dead token again — for ever on the
 * streams with no attempt budget. The user saw a page that looked completely normal and had silently
 * stopped updating.
 *
 * The ordinary API path never had this problem: its 401 interceptor refreshes and replays. A stream gets
 * no such second chance, because a rejected `EventSource` never becomes a request we can retry.
 *
 * ## Why calling this proactively is safe
 *
 * Refreshing is single-flight and convergent, not a race:
 * - in-tab, concurrent callers share one in-flight refresh promise;
 * - across tabs, a Web Locks mutex serialises them and late arrivals adopt the sibling's fresh token;
 * - server-side, the rotation grace window means a token presented twice converges instead of forking
 *   the chain.
 *
 * ## Why failure is swallowed
 *
 * A refresh that fails must not end the session or block the reconnect. If it fails, the reconnect
 * proceeds with the stale token and is rejected exactly as it is today — no worse, and the next backoff
 * tick tries again. Throwing here would turn a transient network blip into a dead stream, and (worse) an
 * unhandled rejection inside a timer callback.
 *
 * Note `shouldRefreshToken()` had ZERO callers before this — the intent existed, the wiring never landed.
 */
export async function ensureFreshAccessTokenForStream(): Promise<void> {
  if (!tokenService.isTokenExpired() && !tokenService.shouldRefreshToken()) return;

  try {
    await refreshAccessToken();
  } catch {
    // Deliberately ignored — see the doc comment above. The reconnect proceeds and either succeeds or
    // is rejected exactly as before; it must never be prevented from trying.
  }
}
